import { formatISO, startOfDay } from 'date-fns';
import React, { useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker/DatePicker';
import Loading from '../../../../components/Loading/Loading';
import Modal from '../../../../components/Modal/Modal';
import {
  AdditionalInsuredInfoListForApplication,
  BusinessInfo,
  useUpdateApplicationAnswersAndQuoteMutation
} from '../../../../generated/graphql';
import { getApplicationFinalizedMessage } from '../../../../utils/isApplicationFinalizedError';
import { EFFECTIVE_DATE_COPY } from '../../effectiveDateCopy';
import {
  formatEffectiveDate,
  getEffectiveDatePickerBounds,
  isOnOrAfterQuoteExpiry,
  isSameCalendarDay,
  parseCalendarDate
} from '../../effectiveDateRules';
import { NewQuoteForApp } from '../../Quote';

interface EffectiveDateEditorPopupProps {
  applicationGroupId: string;
  coverageStartDate: string;
  /** The group's earliest quote expiry: a date on or after it is refused. */
  quoteExpireDate?: string;
  businessInfo: BusinessInfo;
  additionalInsuredInfoListByAppId?: Record<
    string,
    AdditionalInsuredInfoListForApplication['additionalInsuredInfoList']
  >;
  onClose: () => void;
  onSaved: (newQuoteIds: NewQuoteForApp[]) => Promise<void> | void;
}

// MET-468: change the requested effective date from the quote page. Saves
// through updateApplicationAnswersAndQuote, the Buy Now / ApplicantInfo path,
// so a group signed or paid since the page loaded is refused, and the new
// quote ids come back for the purchase flow.
const EffectiveDateEditorPopup: React.FC<EffectiveDateEditorPopupProps> = ({
  applicationGroupId,
  coverageStartDate,
  quoteExpireDate,
  businessInfo,
  additionalInsuredInfoListByAppId,
  onClose,
  onSaved
}) => {
  const today = startOfDay(new Date());
  const { min, max } = getEffectiveDatePickerBounds({
    transactionType: 'New Business',
    coverageStartDate: today,
    today
  });
  const currentDate = parseCalendarDate(coverageStartDate);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    currentDate ?? today
  );
  const [isInvalidDate, setIsInvalidDate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaveFailed, setIsSaveFailed] = useState(false);
  const [
    applicationFinalizedMessage,
    setApplicationFinalizedMessage
  ] = useState<string | undefined>(undefined);
  // The Modal's action button cannot be disabled, so a second click while the
  // first save is in flight is ignored here.
  const isSavingRef = useRef(false);

  const [
    updateApplicationAnswersAndQuote
  ] = useUpdateApplicationAnswersAndQuoteMutation();

  const isValidDate = (date: Date | undefined): date is Date => {
    if (!date || isNaN(date.getTime())) return false;
    const day = startOfDay(date);
    if (day < min || day > max) return false;
    return !(quoteExpireDate && isOnOrAfterQuoteExpiry(day, quoteExpireDate));
  };

  const onChangeDate = (date: Date | null) => {
    const next = date ?? undefined;
    setSelectedDate(next);
    setIsInvalidDate(!isValidDate(next));
  };

  const save = async () => {
    if (isSavingRef.current) return;
    if (!isValidDate(selectedDate)) {
      setIsInvalidDate(true);
      return;
    }
    if (currentDate && isSameCalendarDay(selectedDate, currentDate)) {
      onClose();
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setIsSaveFailed(false);
    setApplicationFinalizedMessage(undefined);

    // Forward existing additional-insured info unchanged so it is not wiped.
    const additionalInsuredInfoListByApplicationId: AdditionalInsuredInfoListForApplication[] = Object.entries(
      additionalInsuredInfoListByAppId ?? {}
    )
      .filter(([, list]) => list != null)
      .map(([appId, additionalInsuredInfoList]) => ({
        applicationId: appId,
        additionalInsuredInfoList
      }));

    const fail = (finalizedMessage?: string) => {
      isSavingRef.current = false;
      setIsSaving(false);
      if (finalizedMessage) {
        setApplicationFinalizedMessage(finalizedMessage);
      } else {
        setIsSaveFailed(true);
      }
    };

    try {
      // Contact fields are echoed exactly as ApplicantInfo does, so only the
      // date changes. The company name goes back verbatim: the resolver
      // compares it strictly, and a trimmed echo would read as a change.
      const { data } = await updateApplicationAnswersAndQuote({
        variables: {
          applicationGroupId,
          newEffectiveDate: formatISO(selectedDate, { representation: 'date' }),
          newCompanyName: businessInfo.companyName,
          newDBAName: businessInfo.dbaName?.trim() || undefined,
          newEmail: businessInfo.email,
          newPhoneNumber: businessInfo.phoneNumber,
          additionalInsuredInfoListByApplicationId
        }
      });
      const result = data?.updateApplicationAnswersAndQuote;
      if (!result?.isUpdateSuccessful) {
        fail();
        return;
      }
      await onSaved((result.newQuoteIds ?? []) as NewQuoteForApp[]);
    } catch (err) {
      fail(getApplicationFinalizedMessage(err));
    }
  };

  if (isSaving) {
    return (
      <Modal>
        <div data-testid="effective-date-editor">
          <Loading />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      closeAction={onClose}
      closeName={EFFECTIVE_DATE_COPY.editorCancel}
      onAction={save}
      actionName={EFFECTIVE_DATE_COPY.editorSave}
    >
      <div data-testid="effective-date-editor">
        {applicationFinalizedMessage ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
            <div>
              <p className="font-bold">Unable to proceed</p>
              <p>{applicationFinalizedMessage}</p>
            </div>
            <button
              className="ml-4 shrink-0 underline font-bold text-red-700"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        ) : null}
        <h2 className="text-xl font-bold mb-2">
          {EFFECTIVE_DATE_COPY.editorTitle}
        </h2>
        <p className="mb-4">{EFFECTIVE_DATE_COPY.editorPrompt}</p>
        <DatePicker
          id="RequestedEffectiveDate"
          name="requestedEffectiveDate"
          inputClass={isInvalidDate ? 'invalid-row' : ''}
          value={selectedDate}
          minDate={min}
          maxDate={max}
          onChange={onChangeDate}
          readOnly={false}
        />
        {isInvalidDate ? (
          <p
            className="text-sm invalid-entry font-bold mt-2"
            data-testid="effective-date-editor-error"
          >
            {EFFECTIVE_DATE_COPY.editorInvalidDate(
              formatEffectiveDate(quoteExpireDate) ?? ''
            )}
          </p>
        ) : null}
        {isSaveFailed ? (
          <p
            className="text-sm invalid-entry font-bold mt-2"
            data-testid="effective-date-editor-save-failed"
          >
            {EFFECTIVE_DATE_COPY.editorSaveFailed}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default EffectiveDateEditorPopup;
